import { ModuleAsset, ModuleFolder } from './module1-data';

export type AiManagerModuleMaterial = {
  id: string;
  sourceLessonId: number;
  name: string;
  type: string;
  description?: string;
  benefit?: string[];
  files: ModuleAsset[];
};

export type AiManagerModuleTopic = Omit<ModuleFolder, 'files'> & {
  number: string;
  materials: AiManagerModuleMaterial[];
};

export const aimanagerModule1Topics: AiManagerModuleTopic[] = [
  {
    id: 'aimanager-topic-1-1',
    number: '1.1',
    name: 'Topic 1.1. The AI Implementation Manager Profession: Area of Responsibility, Mandate, and KPIs',
    date: '06.07.2026',
    time: '8 files',
    materials: [
      {
        id: 'lesson-1',
        sourceLessonId: 1,
        name: 'Video Lecture 1.1',
        type: 'lecture',
        description:
          'This lecture introduces the role of the AI Implementation Manager, emphasizing the importance of bridging business strategy and technology to ensure successful AI project implementation. Students will learn about the key responsibilities of the implementation manager, including the translation of business pain points into actionable AI use cases, coordination of cross-functional teams, and the establishment of measurable KPIs. The session highlights the significance of effective communication with various stakeholders, including executives, IT, security, legal, and HR, to drive the adoption and scalability of AI solutions within organizations.',
        benefit: [
          'Gain expertise in bridging business strategy and technology for effective AI implementation.',
          'Develop skills in translating complex AI concepts for various stakeholder groups.',
          'Learn to assess business pain points and identify suitable AI use cases.',
          'Acquire knowledge on managing risks and compliance in AI projects.',
          'Master the art of measuring and reporting on AI project effectiveness and ROI.',
        ],
        files: [],
      },
      {
        id: 'lesson-2',
        sourceLessonId: 2,
        name: 'Screencast 1.1 (script)',
        type: 'screencast',
        description:
          'This lesson delves into the responsibilities and key performance indicators (KPIs) of an AI Implementation Manager, emphasizing the importance of a RACI matrix in the AI implementation process. Students will learn how to define roles and responsibilities for various stakeholders involved in use case selection, budget approval, and pilot launches. Practical application is encouraged through a task that involves filling out a RACI matrix for specific roles, reinforcing the understanding of accountability and collaboration within AI projects.',
        benefit: [
          'Learn to create and utilize a RACI matrix for effective project management.',
          'Understand the roles and responsibilities in AI implementation processes.',
          'Develop skills for coordinating cross-functional team decisions and approvals.',
          'Gain insights into budget approval processes relevant to AI projects.',
          'Enhance competency in stakeholder communication and engagement during AI initiatives.',
        ],
        files: [
          {
            name: 'raci_matrix_template.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/02-screencast-screencast-1-1-script/files/raci_matrix_template.xlsx',
          },
          {
            name: 'raci_matrix_completed.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/02-screencast-screencast-1-1-script/files/raci_matrix_completed.xlsx',
          },
        ],
      },
      {
        id: 'lesson-3',
        sourceLessonId: 3,
        name: 'Seminar 1.1',
        type: 'seminar',
        description:
          'This seminar explores the role of an AI Implementation Manager, emphasizing the specific responsibilities and performance metrics relevant to various industries, including banking, retail, production, telecom, and pharmaceuticals. Participants will engage in activities to identify and evaluate key performance indicators (KPIs) essential for AI implementation success, compare their findings against industry benchmarks, and reflect on their learning process. This practical approach ensures a deep understanding of how to align AI initiatives with organizational goals and industry standards.',
        benefit: [
          'Develop expertise in selecting relevant KPIs for AI implementations across various industries.',
          'Gain insights into industry-specific challenges and compliance requirements for AI projects.',
          'Enhance analytical skills through KPI comparison and benchmarking exercises.',
          'Improve strategic thinking by identifying and justifying key performance indicators.',
          'Strengthen decision-making capabilities in AI project management through real-world industry applications.',
        ],
        files: [
          {
            name: 'industry_kpi_worksheet.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/03-seminar-seminar-1-1/files/industry_kpi_worksheet.xlsx',
          },
          {
            name: 'benchmark_kpis.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/03-seminar-seminar-1-1/files/benchmark_kpis.xlsx',
          },
          {
            name: 'activity3_reflection.docx',
            kind: 'document',
            size: '38 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/03-seminar-seminar-1-1/files/activity3_reflection.docx',
          },
          {
            name: 'operating_model_longread.docx',
            kind: 'document',
            size: '41 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/03-seminar-seminar-1-1/files/operating_model_longread.docx',
          },
          {
            name: 'independent_work_differences.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/03-seminar-seminar-1-1/files/independent_work_differences.xlsx',
          },
          {
            name: 'governance_frameworks_screencast.xlsx',
            kind: 'excel',
            size: '13 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.1-the-ai-implementation-manager-profession-area-of-responsibility-mandate-and-kpis/03-seminar-seminar-1-1/files/governance_frameworks_screencast.xlsx',
          },
        ],
      },
    ],
  },
  {
    id: 'aimanager-topic-1-2',
    number: '1.2',
    name: 'Topic 1.2. Classic ML/CV/GenAI: Differences in Data, Timing, Cost, and Infrastructure',
    date: '06.07.2026',
    time: '9 files',
    materials: [
      {
        id: 'lesson-4',
        sourceLessonId: 4,
        name: 'Video lecture 1.2',
        type: 'lecture',
        description:
          'This lecture explores the distinctions between classical machine learning (ML), computer vision (CV), and generative artificial intelligence (GenAI), focusing on their data requirements, timelines, costs, and infrastructure needs. Students will learn about the challenges faced in classical ML projects, such as the necessity for extensive labeled data and the complexities of MLOps, as well as the rapid deployment capabilities of GenAI, which leverages pre-trained models. Key criteria for selecting between these approaches are discussed, including data type, project timelines, and the importance of accuracy versus speed. Additionally, the integration of CV within both ML and GenAI contexts is highlighted, emphasizing its role in real-world applications.',
        benefit: [
          'Understand the critical role of data and labeling in successful ML projects.',
          'Learn to differentiate between classical ML and generative AI applications.',
          'Gain insights on infrastructure requirements for effective ML operations (MLOps).',
          'Develop skills to evaluate data types and determine suitable ML approaches.',
          'Master the selection criteria for choosing between classical ML and GenAI solutions.',
        ],
        files: [],
      },
      {
        id: 'lesson-5',
        sourceLessonId: 5,
        name: 'Screencast 1.2',
        type: 'screencast',
        description:
          'This section provides a detailed comparison of the lifecycle differences between traditional machine learning (ML), computer vision (CV), and generative AI (GenAI) projects. Key concepts include the distinct stages of data collection, labeling, model training, and deployment, highlighting how GenAI significantly reduces project timelines from months to weeks by leveraging pre-trained models. Students will also engage with a practical seven-question checklist designed to guide decision-making between ML/CV and GenAI solutions based on project requirements and constraints.',
        benefit: [
          'Understand the critical differences between classic ML, CV, and GenAI life cycles.',
          'Gain skills in evaluating project timelines and costs for AI implementations.',
          'Learn to utilize a practical checklist for decision-making in AI projects.',
          'Develop the ability to choose between ML and GenAI based on project needs.',
          'Enhance competencies in assessing data requirements for various AI approaches.',
        ],
        files: [
          {
            name: 'ml_cv_genai_handout.xlsx',
            kind: 'excel',
            size: '14 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/02-screencast-screencast-1-2/files/ml_cv_genai_handout.xlsx',
          },
          {
            name: 'ml_cv_genai_screens.xlsx',
            kind: 'excel',
            size: '15 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/02-screencast-screencast-1-2/files/ml_cv_genai_screens.xlsx',
          },
        ],
      },
      {
        id: 'lesson-6',
        sourceLessonId: 6,
        name: 'Seminar 1.2',
        type: 'seminar',
        description:
          'This seminar explores the distinctions between classic machine learning (ML), computer vision (CV), and generative AI (GenAI) in terms of data requirements, implementation timing, costs, and infrastructure needs. Through a case study of Marsh McLennan, students will examine the advantages of utilizing a unified GenAI assistant over traditional ML models, highlighting the rapid implementation and significant efficiency gains achieved. Participants will also engage in an analysis of various industries to determine optimal applications for ML versus GenAI, enhancing their decision-making skills in AI project management.',
        benefit: [
          'Understand the key differences between classic ML, CV, and GenAI technologies.',
          'Analyze real-world case studies to identify optimal AI solutions per industry.',
          'Develop skills to assess risks and benefits of ML versus GenAI approaches.',
          'Gain practical experience in justifying AI technology choices for business cases.',
          'Improve decision-making through data-driven insights from AI implementations.',
        ],
        files: [
          {
            name: 'industry_cases_student_worksheet.xlsx',
            kind: 'excel',
            size: '12 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/03-seminar-seminar-1-2/files/industry_cases_student_worksheet.xlsx',
          },
          {
            name: 'self_assessment_worksheet.docx',
            kind: 'document',
            size: '39 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/03-seminar-seminar-1-2/files/self_assessment_worksheet.docx',
          },
          {
            name: 'final_analysis_reference.docx',
            kind: 'document',
            size: '40 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/03-seminar-seminar-1-2/files/final_analysis_reference.docx',
          },
          {
            name: 'industry_cases_table_screen.xlsx',
            kind: 'excel',
            size: '12 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/03-seminar-seminar-1-2/files/industry_cases_table_screen.xlsx',
          },
          {
            name: 'ml_vs_genai_summary_table.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/03-seminar-seminar-1-2/files/ml_vs_genai_summary_table.xlsx',
          },
        ],
      },
      {
        id: 'lesson-7',
        sourceLessonId: 7,
        name: 'Independent work 1.2',
        type: 'independent_work',
        description:
          'This section delves into the distinctions among classic Machine Learning (ML), Computer Vision (CV), and Generative AI (GenAI) in terms of data requirements, project timelines, costs, and necessary infrastructure. Students will analyze the characteristics and requirements for data quality, including the importance of data volume and class balance, as well as explore the various sources of data that can be utilized for these technologies. The comparison table exercise will facilitate a deeper understanding of how these factors influence the implementation of different AI methodologies.',
        benefit: [
          'Understand the fundamental differences between ML, CV, and GenAI technologies.',
          'Develop skills in data preparation and quality assessment for model training.',
          'Learn to identify and source relevant data for specific AI applications.',
          'Gain insights into the infrastructure requirements for implementing AI solutions.',
          'Enhance decision-making skills regarding project timing and cost considerations.',
        ],
        files: [
          {
            name: 'longread_ml_cv_genai.docx',
            kind: 'document',
            size: '40 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/04-independent-work-independent-work-1-2/files/longread_ml_cv_genai.docx',
          },
          {
            name: 'comparison_table_template.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.2-classic-ml-cv-genai-differences-in-data-timing-cost-and-infrastructure/04-independent-work-independent-work-1-2/files/comparison_table_template.xlsx',
          },
        ],
      },
    ],
  },
  {
    id: 'aimanager-topic-1-3',
    number: '1.3',
    name: 'Topic 1.3. LLM in Business Language: Tokens, Hallucinations, Grounding',
    date: '06.07.2026',
    time: '7 files',
    materials: [
      {
        id: 'lesson-8',
        sourceLessonId: 8,
        name: 'Video lecture 1.3',
        type: 'lecture',
        description:
          'This lecture explores the application of large language models (LLMs) in business, emphasizing critical concepts such as tokenization, hallucinations, and the limitations of LLMs in factual accuracy. Students will learn the distinction between LLMs and traditional databases, understanding how LLMs generate probabilistic responses rather than factual information, which can lead to significant risks in business decision-making. Additionally, the lecture covers the importance of token management in cost calculations and contextual limitations when utilizing LLMs for various applications.',
        benefit: [
          'Understand the fundamental differences between LLMs and traditional databases.',
          'Recognize the risks of relying on LLMs for factual accuracy.',
          'Calculate costs associated with token usage in LLM interactions.',
          'Analyze the significance of context windows in LLM performance.',
          'Identify potential pitfalls of LLM hallucinations in business scenarios.',
        ],
        files: [],
      },
      {
        id: 'lesson-9',
        sourceLessonId: 9,
        name: 'Screencast 1.3 (script)',
        type: 'screencast',
        description:
          'This lesson explores the variability in responses generated by language models like ChatGPT when posed with the same query. Key concepts include the influence of temperature settings on answer consistency—high temperatures promoting creativity and variability, while low temperatures yield deterministic responses. Students will engage in hands-on exercises to observe these differences and understand how token generation and grounding affect model outputs.',
        benefit: [
          'Understand how temperature settings affect AI-generated responses and creativity.',
          'Gain practical experience in testing and analyzing AI output consistency.',
          'Develop skills in evaluating the reliability of AI information sources.',
          'Learn to adjust AI parameters for desired response types in business applications.',
          'Enhance critical thinking by comparing different AI-generated answers systematically.',
        ],
        files: [
          {
            name: 'temperature_experiment_worksheet.xlsx',
            kind: 'excel',
            size: '12 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.3-llm-in-business-language-tokens-hallucinations-grounding/02-screencast-screencast-1-3-script/files/temperature_experiment_worksheet.xlsx',
          },
          {
            name: 'temperature_slide.docx',
            kind: 'document',
            size: '39 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.3-llm-in-business-language-tokens-hallucinations-grounding/02-screencast-screencast-1-3-script/files/temperature_slide.docx',
          },
        ],
      },
      {
        id: 'lesson-10',
        sourceLessonId: 10,
        name: 'Seminar 1.3',
        type: 'seminar',
        description:
          'This seminar focuses on essential concepts related to Large Language Models (LLMs) in a business context, including the definitions of tokens, the phenomenon of hallucinations, and the grounding principle. Students will engage in an interactive quiz to assess their understanding of these concepts and explore practical strategies for crafting effective prompts that minimize hallucinations. The seminar emphasizes the importance of precise questioning and reliance on provided documents to ensure accurate responses from LLMs.',
        benefit: [
          'Understand the concept of tokens in LLMs and their business applications.',
          'Recognize and address LLM hallucinations to improve output reliability.',
          'Apply grounding principles to ensure responses are based solely on provided documents.',
          'Formulate effective prompts to minimize errors in LLM responses.',
          'Analyze contract data accurately using LLMs while adhering to defined limits.',
        ],
        files: [
          {
            name: 'seminar_1_3_workbook.docx',
            kind: 'document',
            size: '41 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.3-llm-in-business-language-tokens-hallucinations-grounding/03-seminar-seminar-1-3/files/seminar_1_3_workbook.docx',
          },
          {
            name: 'seminar_1_3_quiz_reference.xlsx',
            kind: 'excel',
            size: '12 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.3-llm-in-business-language-tokens-hallucinations-grounding/03-seminar-seminar-1-3/files/seminar_1_3_quiz_reference.xlsx',
          },
          {
            name: 'seminar_1_3_screencast.docx',
            kind: 'document',
            size: '40 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.3-llm-in-business-language-tokens-hallucinations-grounding/03-seminar-seminar-1-3/files/seminar_1_3_screencast.docx',
          },
        ],
      },
      {
        id: 'lesson-11',
        sourceLessonId: 11,
        name: 'Independent work 1.3',
        type: 'independent_work',
        description:
          'This section explores the critical distinctions between large language models (LLMs) and traditional databases, emphasizing their probabilistic nature and the implications for business operations. Students will learn about the risks associated with LLM outputs, including the potential for "hallucinations" or inaccuracies, through real-life case studies that highlight the consequences of reliance on AI-generated information. The discussion will also cover strategies for effectively managing expectations and ensuring accountability when integrating AI tools in business environments.',
        benefit: [
          'Understand the difference between LLM and traditional databases for effective AI use.',
          'Recognize the risks of LLM hallucinations in professional environments.',
          'Develop skills to manage and set realistic business expectations for GenAI applications.',
          'Learn the importance of verifying AI-generated content for legal and reputational safety.',
          'Gain insights on implementing grounded AI solutions that reduce misinformation risks.',
        ],
        files: [
          {
            name: 'longread_llm_not_database.docx',
            kind: 'document',
            size: '43 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.3-llm-in-business-language-tokens-hallucinations-grounding/04-independent-work-independent-work-1-3/files/longread_llm_not_database.docx',
          },
          {
            name: 'ceo_summary_tables.xlsx',
            kind: 'excel',
            size: '15 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.3-llm-in-business-language-tokens-hallucinations-grounding/04-independent-work-independent-work-1-3/files/ceo_summary_tables.xlsx',
          },
        ],
      },
    ],
  },
  {
    id: 'aimanager-topic-1-4',
    number: '1.4',
    name: "Topic 1.4. Video Lecture: GenAI's Business Value Map: Where It's Already Been Implemented and What's Working",
    date: '06.07.2026',
    time: '9 files',
    materials: [
      {
        id: 'lesson-12',
        sourceLessonId: 12,
        name: 'Video lecture 1.4',
        type: 'lecture',
        description:
          'This lecture explores the business value of generative AI (GenAI) by examining real-world implementations and measurable benefits across various sectors. Students will learn about four key applications: knowledge assistants that enhance expert productivity, AI-driven customer support that improves service efficiency, tools that boost developer productivity in coding, and personalized marketing strategies that create tailored experiences for consumers. Emphasizing a hybrid approach that combines human expertise with AI capabilities, the lesson highlights how GenAI is transforming traditional business operations.',
        benefit: [
          "Gain insights into generative AI's transformative impact on various business operations.",
          'Develop skills to analyze real-world cases of successful AI implementation.',
          'Enhance knowledge of hybrid systems combining human expertise and AI capabilities.',
          'Learn to improve customer support efficiency through AI-driven solutions.',
          'Understand how to boost developer productivity using generative AI tools.',
          'Master personalized marketing strategies utilizing generative AI for large-scale engagement.',
        ],
        files: [],
      },
      {
        id: 'lesson-13',
        sourceLessonId: 13,
        name: 'Screencast 1.4 (script)',
        type: 'screencast',
        description:
          'The GenAI Business Value Map provides an interactive overview of 15 case studies across six industries, highlighting the potential return on investment (ROI) of up to 30% and associated risks. Key concepts covered include practical implementation guidelines, industry-specific applications such as fraud detection in finance and dynamic pricing in retail, as well as an analysis of common challenges such as data quality and regulatory compliance. Students will learn to prioritize AI initiatives based on industry context and assess their effectiveness in real-world scenarios.',
        benefit: [
          'Gain insights into GenAI applications across diverse industries for strategic decision-making.',
          'Understand key risks associated with AI implementation, enhancing risk management skills.',
          'Learn to calculate and interpret ROI metrics to assess AI project feasibility.',
          'Develop strategies for prioritizing AI projects based on industry-specific needs.',
          'Acquire knowledge of implementation guidelines to streamline AI integration in businesses.',
        ],
        files: [
          {
            name: 'genai_value_map_reference.xlsx',
            kind: 'excel',
            size: '12 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/02-screencast-screencast-1-4-script/files/genai_value_map_reference.xlsx',
          },
          {
            name: 'genai_value_map_worksheet.xlsx',
            kind: 'excel',
            size: '16 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/02-screencast-screencast-1-4-script/files/genai_value_map_worksheet.xlsx',
          },
          {
            name: 'genai_value_map_data.xlsx',
            kind: 'excel',
            size: '12 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/02-screencast-screencast-1-4-script/files/genai_value_map_data.xlsx',
          },
        ],
      },
      {
        id: 'lesson-14',
        sourceLessonId: 14,
        name: 'Workshop 1.4 (description)',
        type: 'workshop',
        description:
          'This workshop focuses on the practical application of Generative AI (GenAI) in various business contexts, guiding participants through the analysis of multiple case studies. Students will learn to identify business pain points, technology patterns employed, methods for measuring success, and associated risks in real-world scenarios. By engaging in structured written responses and oral presentations, participants will enhance their analytical and communication skills, preparing them to effectively present AI solutions to management or clients.',
        benefit: [
          'Develop critical analysis skills through case study assessments.',
          'Gain experience in identifying and solving real business problems with AI.',
          'Enhance presentation skills by defending case solutions verbally.',
          'Learn to evaluate technology patterns and their effectiveness in business contexts.',
          'Understand risk management associated with AI implementations in various industries.',
        ],
        files: [
          {
            name: 'workshop_case_study_worksheet.xlsx',
            kind: 'excel',
            size: '15 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/03-workshop-workshop-1-4-description/files/workshop_case_study_worksheet.xlsx',
          },
          {
            name: 'evaluation_criteria_screencast.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/03-workshop-workshop-1-4-description/files/evaluation_criteria_screencast.xlsx',
          },
        ],
      },
      {
        id: 'lesson-15',
        sourceLessonId: 15,
        name: 'Independent work 1.4',
        type: 'independent_work',
        description:
          'This section focuses on the practical application of Generative AI (GenAI) in business, emphasizing its measurable benefits across various industries. Students will explore real-world implementations, analyze potential ROI, assess associated risks, and determine implementation timelines for selected cases from the GenAI Value Map. Key concepts include identifying operational efficiencies, understanding risk factors like data quality and regulatory challenges, and adapting GenAI solutions to fit specific business contexts.',
        benefit: [
          'Gain insights into ROI measurement for Generative AI implementations in various industries.',
          'Develop skills to analyze risks associated with GenAI projects effectively.',
          "Learn to customize GenAI solutions to fit your organization's unique infrastructure and culture.",
          'Enhance your ability to identify and prioritize high-impact GenAI use cases.',
          'Improve efficiency in data collection and financial forecasting through AI tools.',
        ],
        files: [
          {
            name: 'genai_value_map_worksheet.xlsx',
            kind: 'excel',
            size: '16 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/04-independent-work-independent-work-1-4/files/genai_value_map_worksheet.xlsx',
          },
          {
            name: 'genai_value_map_summary.xlsx',
            kind: 'excel',
            size: '12 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/04-independent-work-independent-work-1-4/files/genai_value_map_summary.xlsx',
          },
        ],
      },
      {
        id: 'lesson-16',
        sourceLessonId: 16,
        name: 'Assignment for independent work 1.4',
        type: 'independent_work',
        description:
          "This assignment focuses on the application of GenAI's Business Value Map, guiding students to explore real-world implementations of generative AI across various industries. Participants will analyze three selected cases relevant to their own organizational context, assessing potential return on investment, associated risks, and implementation timelines. Additionally, students will provide justifications for their selections and, for advanced users, calculate project priorities based on a specified formula.",
        benefit: [
          "Gain insights into GenAI's practical applications across various industries.",
          'Develop skills to assess potential ROI for GenAI implementations effectively.',
          'Learn to identify and evaluate risks associated with AI projects.',
          'Enhance critical thinking through case selection and justification processes.',
          'Acquire a framework for prioritizing AI projects based on multiple factors.',
        ],
        files: [
          {
            name: 'genai_value_map_assignment_worksheet.xlsx',
            kind: 'excel',
            size: '16 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/05-independent-work-assignment-for-independent-work-1-4/files/genai_value_map_assignment_worksheet.xlsx',
          },
          {
            name: 'genai_value_map_example_screencast.docx',
            kind: 'document',
            size: '38 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.4-video-lecture-genai-s-business-value-map-where-it-s-already-been-implemented-and-what-s-working/05-independent-work-assignment-for-independent-work-1-4/files/genai_value_map_example_screencast.docx',
          },
        ],
      },
    ],
  },
  {
    id: 'aimanager-topic-1-5',
    number: '1.5',
    name: 'Topic 1.5. AI Maturity Model',
    date: '06.07.2026',
    time: '3 files',
    materials: [
      {
        id: 'lesson-17',
        sourceLessonId: 17,
        name: 'lecture 1.5',
        type: 'lecture',
        description:
          "The AI Maturity Model lesson explores a framework for assessing a company's progression through five maturity levels in AI implementation. Students will learn to identify strategic, engineering, and cultural factors that influence AI maturity, examining common pitfalls and best practices at each level. Key concepts include the significance of structured data usage, the importance of ROI-focused metrics, and fostering a culture that balances innovation with accountability to enhance business outcomes.",
        benefit: [
          'Gain expertise in assessing AI maturity levels for strategic business transformation.',
          'Learn to identify priority steps for advancing AI implementation within organizations.',
          'Develop skills in connecting AI strategy with data engineering and organizational culture.',
          "Acquire knowledge on establishing metrics to measure AI's business impact effectively.",
          'Enhance ability to foster collaboration between technical teams and business stakeholders.',
        ],
        files: [],
      },
      {
        id: 'lesson-18',
        sourceLessonId: 18,
        name: 'Independent work 1.5',
        type: 'independent_work',
        description:
          "This section focuses on the AI Maturity Model, guiding students through a practical exercise to assess their organization's current AI capabilities and identify specific pain points that AI can address. Students will learn to develop a strategic roadmap for AI implementation, ensuring initial actions are cost-effective, while also establishing a cultural framework for AI decision-making within their company. Key concepts include self-assessment, identifying business challenges, and creating measurable success metrics for AI initiatives.",
        benefit: [
          'Develop skills in conducting comprehensive self-assessments for organizational AI readiness.',
          'Learn to identify and articulate specific business pain points that AI can address.',
          'Gain expertise in creating actionable AI transformation roadmaps with budget considerations.',
          'Master the ability to foster a culture of trust and accountability in AI decision-making.',
          'Enhance strategic communication skills for presenting AI initiatives to executive stakeholders.',
        ],
        files: [
          {
            name: 'ai_maturity_checklist.xlsx',
            kind: 'excel',
            size: '16 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.5-ai-maturity-model/02-independent-work-independent-work-1-5/files/ai_maturity_checklist.xlsx',
          },
          {
            name: 'ceo_ai_pitch_template.xlsx',
            kind: 'excel',
            size: '18 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.5-ai-maturity-model/02-independent-work-independent-work-1-5/files/ceo_ai_pitch_template.xlsx',
          },
          {
            name: 'ai_maturity_reference.docx',
            kind: 'document',
            size: '41 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.5-ai-maturity-model/02-independent-work-independent-work-1-5/files/ai_maturity_reference.docx',
          },
        ],
      },
    ],
  },
  {
    id: 'aimanager-topic-1-6',
    number: '1.6',
    name: 'Topic 1.6. 10 Anti-Patterns of GenAI Implementation',
    date: '06.07.2026',
    time: '3 files',
    materials: [
      {
        id: 'lesson-19',
        sourceLessonId: 19,
        name: 'lecture 1.6',
        type: 'lecture',
        description:
          'This lecture focuses on identifying and understanding ten common anti-patterns in Generative AI (GenAI) implementation that can lead to project failures. Students will learn about the importance of aligning AI initiatives with business needs rather than technology-driven approaches, as well as strategies for avoiding pitfalls such as the "Model first, problem later" mindset and the excessive use of Retrieval-Augmented Generation (RAG). Through real-life examples and actionable mitigation plans, participants will gain insights into effective decision-making and project planning in AI deployment.',
        benefit: [
          'Develop the ability to identify and avoid common pitfalls in AI implementation.',
          'Gain skills in conducting effective business case analyses for AI projects.',
          'Learn to prioritize business needs over technology in AI initiatives.',
          'Acquire strategies for optimizing AI tool selection based on specific use cases.',
          'Enhance competence in evaluating the cost-effectiveness of AI solutions.',
        ],
        files: [],
      },
      {
        id: 'lesson-20',
        sourceLessonId: 20,
        name: 'Seminar 1.6. Working with antipatterns',
        type: 'seminar',
        description:
          'This seminar focuses on identifying and addressing common anti-patterns in Generative AI (GenAI) implementation. Students will learn to select three relevant anti-patterns from a list of ten, analyze their potential impact within their organization, and develop tailored mitigation plans. The session emphasizes the importance of proactive monitoring and correction of these pitfalls, enabling participants to enhance their AI project outcomes effectively.',
        benefit: [
          'Develop the ability to identify common anti-patterns in AI implementation projects.',
          'Create effective mitigation plans tailored to specific anti-patterns in your organization.',
          'Enhance your skills in self-assessment and continuous improvement of AI strategies.',
          'Gain insights into prioritizing critical issues for timely resolution in AI projects.',
          'Learn to foster team collaboration for retrospective discussions on implementation challenges.',
        ],
        files: [
          {
            name: 'mitigation_plan_template.xlsx',
            kind: 'excel',
            size: '13 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.6-10-anti-patterns-of-genai-implementation/02-seminar-seminar-1-6-working-with-antipatterns/files/mitigation_plan_template.xlsx',
          },
          {
            name: 'audit_checklist.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.6-10-anti-patterns-of-genai-implementation/02-seminar-seminar-1-6-working-with-antipatterns/files/audit_checklist.xlsx',
          },
          {
            name: 'example_execution.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.6-10-anti-patterns-of-genai-implementation/02-seminar-seminar-1-6-working-with-antipatterns/files/example_execution.xlsx',
          },
        ],
      },
    ],
  },
  {
    id: 'aimanager-topic-1-7',
    number: '1.7',
    name: 'Topic 1.7. Acceptable Use Policy (AUP)',
    date: '06.07.2026',
    time: '3 files',
    materials: [
      {
        id: 'lesson-21',
        sourceLessonId: 21,
        name: 'lecture 1.7',
        type: 'lecture',
        description:
          'This lecture focuses on the development of an Acceptable Use Policy (AUP) for generative artificial intelligence (GenAI) within the corporate environment. Students will learn about the principles governing the responsible use of GenAI tools, including human responsibility, transparency, mandatory verification of outputs, data minimization, and the need for continuous learning. The session emphasizes the importance of safeguarding confidential information, mitigating legal risks, and ensuring all employees adhere to the established guidelines for using GenAI technologies effectively and ethically.',
        benefit: [
          'Develop skills to create an effective GenAI usage policy for employees.',
          'Gain knowledge on the legal implications of GenAI tool usage.',
          'Learn to verify GenAI outputs to prevent misinformation and errors.',
          'Understand principles of data minimization to protect sensitive information.',
          'Enhance awareness of ongoing training requirements for ethical GenAI use.',
        ],
        files: [],
      },
      {
        id: 'lesson-22',
        sourceLessonId: 22,
        name: 'Assignment (independent work, 1.5 hours):',
        type: 'independent_work',
        description:
          'This assignment focuses on adapting an Acceptable Use Policy (AUP) template specifically for a company, guiding students through the completion of ten critical sections, including purpose, definitions, general principles, and permitted tools. Students will develop a comprehensive understanding of the implications of AI use within an organization, emphasizing responsibility, data protection, and compliance with corporate standards. Additionally, they will create a concise presentation to effectively communicate the AUP to employees, ensuring clarity and adherence to guidelines.',
        benefit: [
          'Learn to customize an Acceptable Use Policy for your organization.',
          'Develop skills to deliver concise presentations on policy topics.',
          'Understand the importance of data protection and compliance in AI usage.',
          'Gain knowledge in defining key terms related to AI and data security.',
          'Cultivate awareness of ethical responsibilities and prohibited actions in AI use.',
        ],
        files: [
          {
            name: 'aup_template.xlsx',
            kind: 'excel',
            size: '18 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.7-acceptable-use-policy-aup/02-independent-work-assignment-independent-work-1-5-hours/files/aup_template.xlsx',
          },
          {
            name: 'aup_presentation_script.docx',
            kind: 'document',
            size: '40 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.7-acceptable-use-policy-aup/02-independent-work-assignment-independent-work-1-5-hours/files/aup_presentation_script.docx',
          },
          {
            name: 'self_checklist.xlsx',
            kind: 'excel',
            size: '11 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.7-acceptable-use-policy-aup/02-independent-work-assignment-independent-work-1-5-hours/files/self_checklist.xlsx',
          },
        ],
      },
    ],
  },
  {
    id: 'aimanager-topic-1-8',
    number: '1.8',
    name: 'Topic 1.8. Quick Data Readiness',
    date: '06.07.2026',
    time: '5 files',
    materials: [
      {
        id: 'lesson-23',
        sourceLessonId: 23,
        name: 'lecture 1.8',
        type: 'lecture',
        description:
          'This lecture focuses on evaluating data readiness for generative AI (GenAI) projects, emphasizing the critical role of structured and unstructured data. Students will learn to assess their data environment by answering five key questions, which will help identify potential risks associated with data organization and accessibility. The session covers the importance of data centralization, the necessity of digitizing unstructured data, and the implications of data silos, equipping participants with practical strategies for ensuring their data is ready for successful GenAI implementation.',
        benefit: [
          'Learn to assess data readiness swiftly for GenAI projects in just 15 minutes.',
          'Identify critical risks associated with data management in generative AI implementations.',
          'Understand the importance of structured and unstructured data for AI effectiveness.',
          'Gain skills in data centralization and integration across different departments.',
          'Develop strategies for digitizing and organizing data for optimal AI performance.',
        ],
        files: [],
      },
      {
        id: 'lesson-24',
        sourceLessonId: 24,
        name: 'Seminar 1.8. Practical Task',
        type: 'seminar',
        description:
          'This seminar focuses on conducting a rapid assessment of data readiness for Generative AI (GenAI) projects through a structured 10-step checklist. Participants will learn to evaluate real, training, or hypothetical cases by identifying data sources, assessing quality and accessibility, and assigning responsibilities across various departments. The exercise culminates in a comprehensive evaluation that utilizes color coding to determine project readiness, ensuring students gain practical skills in data management and project preparedness.',
        benefit: [
          'Develop skills in conducting rapid data readiness assessments for AI projects.',
          'Gain experience in creating and utilizing a 10-step checklist for data evaluation.',
          'Learn to identify and categorize data quality issues effectively.',
          'Enhance teamwork abilities through collaborative case assessments in small groups.',
          'Acquire knowledge of assigning responsibilities across various departments for project success.',
        ],
        files: [
          {
            name: 'blank_assessment_template.xlsx',
            kind: 'excel',
            size: '13 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.8-quick-data-readiness/02-seminar-seminar-1-8-practical-task/files/blank_assessment_template.xlsx',
          },
          {
            name: 'completed_example_chatbot.xlsx',
            kind: 'excel',
            size: '13 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.8-quick-data-readiness/02-seminar-seminar-1-8-practical-task/files/completed_example_chatbot.xlsx',
          },
          {
            name: 'checklist_display.xlsx',
            kind: 'excel',
            size: '13 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.8-quick-data-readiness/02-seminar-seminar-1-8-practical-task/files/checklist_display.xlsx',
          },
        ],
      },
      {
        id: 'lesson-25',
        sourceLessonId: 25,
        name: 'MODULE 1  CONTROL FORM',
        type: 'control_form',
        description:
          "This module focuses on the practical aspects of crafting an operating brief for an AI Implementation Manager. Students will learn to define the manager's mandate, establish key performance indicators (KPIs), and identify stakeholder roles along with their expectations and associated risks. Emphasis is placed on articulating the implementation context and preparing for escalation to senior management when necessary, ensuring comprehensive understanding of the AI integration process.",
        benefit: [
          'Develop the ability to create concise operating briefs for AI implementation.',
          'Gain insight into stakeholder roles and their expectations in AI projects.',
          'Learn to define and measure key performance indicators (KPIs) for AI initiatives.',
          'Understand risk management and escalation processes in AI implementations.',
          'Master the coordination of AI use cases from identification to measurable outcomes.',
        ],
        files: [
          {
            name: 'operating_brief_template.xlsx',
            kind: 'excel',
            size: '13 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.8-quick-data-readiness/03-control-form-module-1-control-form/files/operating_brief_template.xlsx',
          },
          {
            name: 'assignment_and_test_slides.docx',
            kind: 'document',
            size: '40 KB',
            s3Key:
              'edu/source-212-8-229-145/module-01/topic-1.8-quick-data-readiness/03-control-form-module-1-control-form/files/assignment_and_test_slides.docx',
          },
        ],
      },
    ],
  },
];

export const aimanagerModule1Assets: ModuleAsset[] =
  aimanagerModule1Topics.flatMap((topic) =>
    topic.materials.flatMap((material) => material.files)
  );
